<template>
  <v-container>
    <v-row class="mb-6">
      <v-col cols="12" sm="6">
        <h1>Clients</h1>
        <p class="text-body2 text-medium-emphasis">Booked and prospective clients</p>
      </v-col>
      <v-col cols="12" sm="6" class="text-right">
        <v-btn
          variant="tonal"
          class="mr-2"
          :loading="store.isImporting"
          @click="importFromIn"
        >
          <v-icon start>mdi-download</v-icon>
          Import from Invoice Ninja
        </v-btn>
        <v-btn color="primary" @click="openCreate">
          <v-icon start>mdi-account-plus</v-icon>
          Add Client
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="store.error" type="error" closable class="mb-4" @click:close="store.error = null">
      {{ store.error }}
    </v-alert>
    <v-alert
      v-if="importSummary"
      type="success"
      closable
      class="mb-4"
      @click:close="importSummary = null"
    >
      {{ importSummary }}
    </v-alert>

    <v-row v-if="store.isLoading">
      <v-col cols="12"><v-skeleton-loader type="table-tbody" /></v-col>
    </v-row>

    <v-row v-else-if="store.clients.length === 0">
      <v-col cols="12">
        <v-card>
          <v-card-text class="text-center py-12">
            <v-icon size="48" class="text-medium-emphasis mb-4">mdi-domain</v-icon>
            <p class="text-h6">No clients yet</p>
            <p class="text-body2 text-medium-emphasis">
              Import from Invoice Ninja or add one manually
            </p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col v-for="client in store.clients" :key="client.id" cols="12" md="6" lg="4">
        <v-card class="h-100">
          <v-card-item>
            <template #title>{{ client.name }}</template>
            <template #subtitle>
              <v-chip
                size="x-small"
                :color="client.invoiceNinjaClientId ? 'success' : undefined"
              >
                {{ client.invoiceNinjaClientId ? 'In Invoice Ninja' : 'Local only' }}
              </v-chip>
            </template>
            <template #append>
              <v-btn
                icon="mdi-pencil"
                size="small"
                variant="text"
                color="primary"
                @click="openEdit(client)"
              />
              <v-btn
                icon="mdi-delete"
                size="small"
                variant="text"
                color="error"
                @click="removeClient(client.id)"
              />
            </template>
          </v-card-item>
          <v-card-text>
            <p v-if="client.contactEmail" class="text-body2">{{ client.contactEmail }}</p>
            <p v-if="client.contactPhone" class="text-body2">{{ client.contactPhone }}</p>
            <p v-if="formatAddress(client.address)" class="text-body2 text-medium-emphasis">
              {{ formatAddress(client.address) }}
            </p>
            <v-btn
              size="small"
              variant="tonal"
              class="mt-3"
              :loading="store.pushingId === client.id"
              @click="pushClient(client.id)"
            >
              <v-icon start size="small">mdi-upload</v-icon>
              {{ client.invoiceNinjaClientId ? 'Update in Invoice Ninja' : 'Push to Invoice Ninja' }}
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showDialog" width="500">
      <v-card>
        <v-card-title>{{ editingId ? 'Edit Client' : 'Add Client' }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="save">
            <v-text-field v-model="form.name" label="Name" required class="mb-3" />
            <v-text-field v-model="form.contactEmail" label="Email" type="email" class="mb-3" />
            <v-text-field v-model="form.contactPhone" label="Phone" class="mb-3" />
            <v-text-field v-model="form.street" label="Street" class="mb-3" />
            <v-row dense class="mb-1">
              <v-col cols="6"><v-text-field v-model="form.city" label="City" /></v-col>
              <v-col cols="3"><v-text-field v-model="form.state" label="State" /></v-col>
              <v-col cols="3"><v-text-field v-model="form.postalCode" label="Zip" /></v-col>
            </v-row>
            <div class="text-right">
              <v-btn text @click="showDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit" :loading="saving">Save</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import type { Client, Address } from '@gigdriver/domain';
import { useClientsStore } from '../stores/clients';

const route = useRoute();
const store = useClientsStore();

const showDialog = ref(false);
const saving = ref(false);
const editingId = ref<string | null>(null);
const importSummary = ref<string | null>(null);

const form = reactive({
  name: '',
  contactEmail: '',
  contactPhone: '',
  street: '',
  city: '',
  state: '',
  postalCode: '',
});

const orgId = () => route.query.org as string;

onMounted(() => {
  if (orgId()) store.loadOrgClients(orgId());
});

function formatAddress(a?: Address): string {
  if (!a) return '';
  const line2 = [a.city, a.state, a.postalCode].filter(Boolean).join(' ');
  return [a.street, line2].filter(Boolean).join(', ');
}

function resetForm() {
  form.name = '';
  form.contactEmail = '';
  form.contactPhone = '';
  form.street = '';
  form.city = '';
  form.state = '';
  form.postalCode = '';
}

function openCreate() {
  editingId.value = null;
  resetForm();
  showDialog.value = true;
}

function openEdit(client: Client) {
  editingId.value = client.id;
  form.name = client.name;
  form.contactEmail = client.contactEmail ?? '';
  form.contactPhone = client.contactPhone ?? '';
  form.street = client.address?.street ?? '';
  form.city = client.address?.city ?? '';
  form.state = client.address?.state ?? '';
  form.postalCode = client.address?.postalCode ?? '';
  showDialog.value = true;
}

async function save() {
  try {
    saving.value = true;
    const address: Address = {
      street: form.street || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      postalCode: form.postalCode || undefined,
    };
    const hasAddress = Object.values(address).some(Boolean);
    const payload = {
      name: form.name,
      contactEmail: form.contactEmail || undefined,
      contactPhone: form.contactPhone || undefined,
      address: hasAddress ? address : undefined,
    };
    if (editingId.value) {
      await store.updateClient(orgId(), editingId.value, payload);
    } else {
      await store.createClient(orgId(), payload);
    }
    showDialog.value = false;
  } catch {
    // store.error is set
  } finally {
    saving.value = false;
  }
}

async function removeClient(id: string) {
  if (!confirm('Delete this client?')) return;
  await store.deleteClient(orgId(), id);
}

async function pushClient(clientId: string) {
  try {
    await store.pushToInvoiceNinja(orgId(), clientId);
  } catch {
    // store.error is set
  }
}

async function importFromIn() {
  importSummary.value = null;
  try {
    const r = await store.importFromInvoiceNinja(orgId());
    importSummary.value = `Imported ${r.imported} new, updated ${r.updated} (of ${r.total}).`;
  } catch {
    // store.error is set
  }
}
</script>
